import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Pricing() {
  const { t } = useI18n();
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="glow-blob bg-[#EC008C] w-[420px] h-[420px] top-20 -right-32 opacity-30" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-16">
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FF1F87]">
            {t.pricing.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            {t.pricing.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-2xl">
            {t.pricing.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {t.pricing.plans.map((p, idx) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative rounded-3xl p-8 flex flex-col ${
                p.highlight
                  ? "bg-gradient-to-b from-[#1a0010] to-[#0A0A0A] border-2 border-[#EC008C] shadow-[0_0_60px_-10px_rgba(236,0,140,0.45)] lg:scale-[1.03]"
                  : "bg-[#0A0A0A] border border-white/10"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#EC008C] text-[10px] uppercase tracking-[0.2em] font-bold text-white flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Más popular
                </div>
              )}

              <h3 className="font-display text-2xl font-bold text-white">{p.name}</h3>
              <p className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
                {p.for}
              </p>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl font-black chrome-text">
                  {p.price}
                </span>
                <span className="text-sm text-zinc-400">{t.pricing.monthly}</span>
              </div>

              <ul className="mt-7 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span
                      className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        p.highlight ? "bg-[#EC008C]" : "bg-white/10"
                      }`}
                    >
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="#book"
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 text-center px-6 py-3.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                  p.highlight
                    ? "bg-[#EC008C] hover:bg-[#FF1F87] text-white shadow-[0_8px_30px_rgba(236,0,140,0.35)]"
                    : "bg-white text-black hover:bg-zinc-200"
                }`}
              >
                {t.pricing.cta}
              </a>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-xs text-zinc-500 text-center max-w-2xl mx-auto">
          {t.pricing.note}
        </p>
      </div>
    </section>
  );
}
